import React, { useState, useEffect } from "react";
import { useLang } from "@/context/LangContext";
import { NAV } from "@/constants/testIds";
import { Menu, X } from "lucide-react";

const Header = () => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "about", testid: NAV.about, label: t.nav.about },
    { id: "services", testid: NAV.services, label: t.nav.services },
    { id: "blog", testid: NAV.blog, label: t.nav.blog },
    { id: "contact", testid: NAV.contact, label: t.nav.contact },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#FBF9F6]/85 border-b border-[#D4AF37]/25"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 flex items-center justify-between h-20">
        {/* Brand */}
        <a
          href="#top"
          data-testid={NAV.brand}
          className="flex flex-col leading-none"
        >
          <span className="overline text-[#66023C]" style={{ color: "var(--imperial)" }}>
            Cabinet Avocat
          </span>
          <span className="serif text-lg md:text-xl tracking-tight text-[#1A0010]">
            Martiș Biatrice-Olimpia
          </span>
        </a>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.id}
              data-testid={l.testid}
              href={`#${l.id}`}
              className="text-[12px] tracking-[0.18em] uppercase font-medium text-[#1A0010] hover:text-[#66023C] transition-colors relative group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-[1px] w-0 bg-[#D4AF37] group-hover:w-full transition-all duration-300" />
            </a>
          ))}
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-5">
          <div className="hidden md:flex items-center gap-1 text-[12px] tracking-[0.18em] uppercase font-medium">
            <button
              data-testid={NAV.langRo}
              onClick={() => setLang("ro")}
              className={`px-2 py-1 transition-colors ${
                lang === "ro" ? "text-[#66023C] border-b border-[#D4AF37]" : "text-[#4D3D46] hover:text-[#1A0010]"
              }`}
            >
              RO
            </button>
            <span className="text-[#D4AF37]">/</span>
            <button
              data-testid={NAV.langEn}
              onClick={() => setLang("en")}
              className={`px-2 py-1 transition-colors ${
                lang === "en" ? "text-[#66023C] border-b border-[#D4AF37]" : "text-[#4D3D46] hover:text-[#1A0010]"
              }`}
            >
              EN
            </button>
          </div>
          <a
            href="#contact"
            data-testid={NAV.bookCta}
            className="hidden md:inline-flex btn-imperial !py-2.5 !px-4 text-[10.5px]"
          >
            {t.nav.bookCta}
          </a>
          <button
            className="lg:hidden text-[#1A0010]"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden bg-[#FBF9F6] border-t border-[#D4AF37]/30 px-6 py-6">
          <nav className="flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={() => setOpen(false)}
                className="text-[13px] tracking-[0.18em] uppercase font-medium text-[#1A0010]"
              >
                {l.label}
              </a>
            ))}
            <div className="flex items-center gap-3 pt-3 border-t border-[#D4AF37]/25">
              <button
                data-testid={`${NAV.langRo}-mobile`}
                onClick={() => setLang("ro")}
                className={lang === "ro" ? "text-[#66023C] font-semibold" : "text-[#4D3D46]"}
              >
                RO
              </button>
              <span className="text-[#D4AF37]">/</span>
              <button
                data-testid={`${NAV.langEn}-mobile`}
                onClick={() => setLang("en")}
                className={lang === "en" ? "text-[#66023C] font-semibold" : "text-[#4D3D46]"}
              >
                EN
              </button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
